import OlMap from 'ol/Map';
import Feature, { FeatureLike } from 'ol/Feature';
import { Coordinate } from 'ol/coordinate';
import { Circle as CircleGeometry, Geometry, LineString, Point, Polygon } from 'ol/geom';
import { Draw } from 'ol/interaction';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { getArea, getLength } from 'ol/sphere';
import { Circle as CircleStyle, Fill, RegularShape, Stroke, Style, Text } from 'ol/style';
import { Subject } from 'rxjs';
import { Z_INDEX } from '../site-map.constants';

export type MeasureMode = 'line' | 'area' | 'circle';

export interface DrawnCircle {
  center: Coordinate;
  radius: number;
}

const DRAW_TYPE = { line: 'LineString', area: 'Polygon', circle: 'Circle' } as const;

const SHAPE_STYLE = new Style({
  fill: new Fill({ color: 'rgba(100, 100, 255, 0.4)' }),
  stroke: new Stroke({ color: 'rgba(0, 153, 255, 1)', width: 3 }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.7)' }),
    fill: new Fill({ color: 'rgba(255, 255, 255, 0.2)' }),
  }),
});

const CIRCLE_STYLE = new Style({
  fill: new Fill({ color: 'rgba(255, 100, 100, 0.3)' }),
  stroke: new Stroke({ color: 'rgba(255, 0, 0, 1)', lineDash: [5, 5], width: 3 }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({ color: 'rgba(255, 0, 0, 0.8)' }),
    fill: new Fill({ color: 'rgba(255, 255, 255, 0.2)' }),
  }),
});

const RADIUS_LINE_STYLE = new Style({
  stroke: new Stroke({ color: 'rgba(255, 0, 0, 0.8)', width: 2, lineDash: [2, 4] }),
});

const SEGMENT_STYLE = new Style({
  text: new Text({
    font: '12px Calibri,sans-serif',
    fill: new Fill({ color: 'rgba(255, 255, 255, 1)' }),
    backgroundFill: new Fill({ color: 'rgba(0, 0, 0, 0.4)' }),
    padding: [2, 2, 2, 2],
    textBaseline: 'bottom',
    offsetY: -12,
  }),
  image: new RegularShape({
    radius: 6,
    points: 3,
    angle: Math.PI,
    displacement: [0, 8],
    fill: new Fill({ color: 'rgba(0, 0, 0, 0.4)' }),
  }),
});

const LABEL_STYLE = new Style({
  text: new Text({
    font: '14px Calibri,sans-serif',
    fill: new Fill({ color: 'rgba(255, 255, 255, 1)' }),
    backgroundFill: new Fill({ color: 'rgba(0, 0, 0, 0.7)' }),
    padding: [3, 3, 3, 3],
    textBaseline: 'bottom',
    offsetY: -15,
  }),
  image: new RegularShape({
    radius: 8,
    points: 3,
    angle: Math.PI,
    displacement: [0, 10],
    fill: new Fill({ color: 'rgba(0, 0, 0, 0.7)' }),
  }),
});

/**
 * Calque de tracé : règle (ligne), polygone et cercle. Il affiche la forme en cours avec ses longueurs et sa surface,
 * puis la garde à l'écran jusqu'à `stop()`. Les outils de création s'en servent pour récupérer la géométrie dessinée.
 */
export class MeasureLayer {
  readonly polygonDrawn = new Subject<Coordinate[][]>();
  readonly circleDrawn = new Subject<DrawnCircle>();

  private readonly source = new VectorSource<Feature>();
  private readonly layer = new VectorLayer({
    source: this.source,
    zIndex: Z_INDEX.measure,
    style: feature => this.styleFor(feature, true),
  });
  private draw: Draw | null = null;
  private mode: MeasureMode | null = null;

  constructor(private readonly map: OlMap) {
    map.addLayer(this.layer);
  }

  start(mode: MeasureMode): void {
    if (this.mode === mode) return;
    this.stop();
    this.mode = mode;

    const draw = new Draw({
      source: this.source,
      type: DRAW_TYPE[mode],
      style: feature => this.styleFor(feature, mode !== 'circle', DRAW_TYPE[mode]),
    });
    draw.on('drawstart', () => this.source.clear());
    draw.on('drawend', event => this.onDrawEnd(mode, event.feature.getGeometry()));
    this.draw = draw;
    this.map.addInteraction(draw);
  }

  /** Garde la forme tracée à l'écran sans permettre d'en dessiner une autre. */
  suspend(): void {
    this.draw?.setActive(false);
  }

  stop(): void {
    if (this.draw) this.map.removeInteraction(this.draw);
    this.draw = null;
    this.mode = null;
    this.source.clear();
  }

  dispose(): void {
    this.stop();
    this.map.removeLayer(this.layer);
  }

  private onDrawEnd(mode: MeasureMode, geometry: Geometry | undefined): void {
    if (mode === 'area' && geometry instanceof Polygon) {
      this.polygonDrawn.next(geometry.getCoordinates());
    } else if (mode === 'circle' && geometry instanceof CircleGeometry) {
      this.circleDrawn.next({ center: geometry.getCenter(), radius: geometry.getRadius() });
    }
  }

  /** `drawType` restreint le style à la forme en cours de tracé (les points de sommets sont toujours stylés). */
  private styleFor(feature: FeatureLike, segments: boolean, drawType?: string): Style[] {
    const geometry = (feature as Feature).getGeometry();
    if (!geometry) return [];
    const type = geometry.getType();
    if (drawType && drawType !== type && type !== 'Point') return [];

    if (geometry instanceof CircleGeometry) return this.circleStyles(geometry);
    if (geometry instanceof Polygon) return this.polygonStyles(geometry, segments);
    if (geometry instanceof LineString) return this.lineStyles(geometry, segments);
    return [];
  }

  private circleStyles(circle: CircleGeometry): Style[] {
    const styles = [CIRCLE_STYLE];
    const center = circle.getCenter();
    const radius = circle.getRadius();
    if (radius <= 0) return styles;

    const radiusLine = new LineString([center, [center[0] + radius, center[1]]]);
    const radiusLineStyle = RADIUS_LINE_STYLE.clone();
    radiusLineStyle.setGeometry(radiusLine);
    styles.push(radiusLineStyle);
    styles.push(this.labelAt(new Point(radiusLine.getCoordinateAt(0.5)), `R: ${formatLength(radiusLine)}`));

    const geodesicRadius = getLength(radiusLine);
    const areaLabel = this.labelAt(new Point(center), formatSurface(Math.PI * geodesicRadius * geodesicRadius));
    areaLabel.getText()?.setOffsetY(-35);
    styles.push(areaLabel);
    return styles;
  }

  private polygonStyles(polygon: Polygon, segments: boolean): Style[] {
    const styles = [SHAPE_STYLE, this.labelAt(polygon.getInteriorPoint(), formatSurface(getArea(polygon)))];
    if (segments) styles.push(...this.segmentStyles(new LineString(polygon.getCoordinates()[0])));
    return styles;
  }

  private lineStyles(line: LineString, segments: boolean): Style[] {
    const styles = [SHAPE_STYLE, this.labelAt(new Point(line.getLastCoordinate()), formatLength(line))];
    if (segments) styles.push(...this.segmentStyles(line));
    return styles;
  }

  private segmentStyles(line: LineString): Style[] {
    const styles: Style[] = [];
    line.forEachSegment((start, end) => {
      const segment = new LineString([start, end]);
      const style = SEGMENT_STYLE.clone();
      style.setGeometry(new Point(segment.getCoordinateAt(0.5)));
      style.getText()?.setText(formatLength(segment));
      styles.push(style);
    });
    return styles;
  }

  private labelAt(point: Point, text: string): Style {
    const style = LABEL_STYLE.clone();
    style.setGeometry(point);
    style.getText()?.setText(text);
    return style;
  }
}

function formatLength(line: LineString): string {
  const length = getLength(line);
  return length > 1000 ? `${Math.round((length / 1000) * 100) / 100} km` : `${Math.round(length * 100) / 100} m`;
}

function formatSurface(area: number): string {
  return area > 1_000_000 ? `${Math.round((area / 1_000_000) * 100) / 100} km²` : `${Math.round(area * 100) / 100} m²`;
}
